import React, { useState, useCallback, useEffect, useRef } from "react";
import { compactJobDescriptionForParsing } from "../../../shared/utils";
import { useRenderGuard } from "../hooks/useRenderGuard";
import "./ApplicationWizard.css";
import { secureOpenAIService } from "../services/secureOpenaiService";
import {
  jobApplicationService,
  ApplicationNote,
} from "../services/jobApplicationService";
import { useMessageBox, MessageBox } from "./MessageBox";
import { JobApplicationForm } from "./JobApplicationForm";

export interface Education {
  degree: string | null;
  field: string | null;
  requirement: "required" | "desired" | "both" | null;
}

export interface BenefitsStructured {
  healthInsurance: string[];
  retirement: string[];
  timeOff: string[];
  allowances: string[];
  memberships: string[];
  bonusesEquity: string[];
  other: string[];
}

export interface Contact {
  id?: string;
  name: string | null;
  title: string | null;
  email: string | null;
  phone: string | null;
  linkedinUrl?: string | null;
  contactType:
    | "job_poster"
    | "recruiter"
    | "hiring_manager"
    | "interviewer"
    | "other";
  notes: string | null;
  extracted?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface JobApplication {
  id?: string;
  url: string;
  company: string | null;
  position: string | null;
  positionCore: string | null;
  positionSpecialization: string[];
  salary: string | null;
  location: string | null;
  workType: "remote" | "hybrid" | "on-site" | null;
  employmentType:
    | "Full-time"
    | "Part-time"
    | "Contract"
    | "Temporary"
    | "Internship"
    | "Volunteer"
    | "Other"
    | null;
  contactInfo: string | null;
  recruiter: string | null;
  jobLevel: string | null;
  jobLevelNormalized: string | null;
  jobLevelConfidence: number | null;
  jobLevelSource: string | null;
  jobSummary: string | null;
  responsibilities: string[];
  requiredQualifications: string[];
  optionalQualifications: string[];
  additionalQualifications: string[];
  education: Education[];
  technologies: string[];
  benefits: string[];
  benefitsStructured: BenefitsStructured;
  postingDate: string | null;
  postingAge: string | null;
  applicantsCount: number | null;
  applicantsText: string | null;
  description: string;
  status:
    | "Initialized"
    | "Pending"
    | "Applied"
    | "Interviewing"
    | "Offer"
    | "Rejected";
  appliedDate: string;
  notes?: ApplicationNote[];
  notes_count?: number;
  contacts?: Contact[];
  createdAt?: string;
  updatedAt?: string;
  guidedPrepSummary?:
    | import("../services/jobApplicationService").GuidedInterviewPrepSummary
    | null;
  guidedPrepSummaryAvailable?: boolean | null;
  fullGuidedPrepSummary?:
    | import("../services/jobApplicationService").GuidedInterviewPrepSummary
    | null;
  fullGuidedPrepSummaryAvailable?: boolean | null;
  // Additional properties for JobAnalysisDisplay
  potentialGaps?: string[];
  recommendations?: string[];
  coverLetterContent?: string;
  nextSteps?: string[];
  jobDescription?: string;
  fitScore?: number;
  techFit?: number;
  experienceMatch?: number;
  selectedResumeId?: string | null;
  resumeSelectionType?: "saved" | "custom" | null;
  customResumeStatus?:
    | "not_started"
    | "wizard_in_progress"
    | "generated"
    | "editing"
    | "failed"
    | null;
  jobAnalysis?: {
    fitScore: number;
    techFit: number;
    experienceMatch: number;
    potentialGaps: string[];
    recommendations: string[];
    responsibilities: string[];
    benefits: string[];
    education: Education[];
    nextSteps: string[];
  };
  interview_prep_data?: import("../services/jobApplicationService").InterviewPrepResponse;
  company_intelligence?: import("../services/jobApplicationService").CompanyIntelligenceResponse;
  resumeId?: string | null;
}

interface ApplicationWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (application: JobApplication) => void;
  editingApplication?: JobApplication | null;
  viewMode?: boolean;
}

export const ApplicationWizard: React.FC<ApplicationWizardProps> = ({
  isOpen,
  onClose,
  onSave,
  editingApplication = null,
  viewMode = false,
}) => {
  useRenderGuard("ApplicationWizard");
  // Start at step 2 for editing (review/edit), step 1 for new applications (job description input)
  // Users can navigate back to step 1 when editing to re-parse if needed
  const [currentStep, setCurrentStep] = useState(editingApplication ? 2 : 1);
  const [isProcessing, setIsProcessing] = useState(false);
  const { messageBox, showMessageBox, hideMessageBox } = useMessageBox();
  // Note: fitAnalysis and companyIntelligence are now managed by JobApplicationForm (Step 2)

  // Track last initialized application ID to prevent unnecessary re-initialization
  const lastInitializedAppIdRef = useRef<string | null>(null);
  const lastInitializedViewModeRef = useRef<boolean>(false);

  // Track if modal should animate (only on first open, not on re-renders)
  const [shouldAnimate, setShouldAnimate] = useState<boolean>(false);

  const [formData, setFormData] = useState<Partial<JobApplication>>({
    url: editingApplication?.url || "",
    description: editingApplication?.description || "",
    company: editingApplication?.company || null,
    position: editingApplication?.position || null,
    positionCore: editingApplication?.positionCore || null,
    positionSpecialization: editingApplication?.positionSpecialization || [],
    salary: editingApplication?.salary || null,
    location: editingApplication?.location || null,
    workType: editingApplication?.workType || null,
    employmentType: editingApplication?.employmentType || null,
    contactInfo: editingApplication?.contactInfo || null,
    recruiter: editingApplication?.recruiter || null,
    jobLevel: editingApplication?.jobLevel || null,
    jobLevelNormalized: editingApplication?.jobLevelNormalized || null,
    jobLevelConfidence: editingApplication?.jobLevelConfidence || null,
    jobLevelSource: editingApplication?.jobLevelSource || null,
    jobSummary: editingApplication?.jobSummary || null,
    responsibilities: editingApplication?.responsibilities || [],
    requiredQualifications: editingApplication?.requiredQualifications || [],
    optionalQualifications: editingApplication?.optionalQualifications || [],
    additionalQualifications:
      editingApplication?.additionalQualifications || [],
    education: editingApplication?.education || [],
    technologies: editingApplication?.technologies || [],
    benefits: editingApplication?.benefits || [],
    benefitsStructured: editingApplication?.benefitsStructured || {
      healthInsurance: [],
      retirement: [],
      timeOff: [],
      allowances: [],
      memberships: [],
      bonusesEquity: [],
      other: [],
    },
    fitScore: editingApplication?.fitScore ?? undefined,
    techFit: editingApplication?.techFit ?? undefined,
    experienceMatch: editingApplication?.experienceMatch ?? undefined,
    jobAnalysis: editingApplication?.jobAnalysis ?? undefined,
    contacts: editingApplication?.contacts || [],
    postingDate: editingApplication?.postingDate || null,
    postingAge: editingApplication?.postingAge || null,
    applicantsCount: editingApplication?.applicantsCount || null,
    applicantsText: editingApplication?.applicantsText || null,
    status: editingApplication?.status || "Pending",
    appliedDate:
      editingApplication?.appliedDate || new Date().toISOString().split("T")[0],
    notes: editingApplication?.notes ?? undefined,
  });

  // Note: fitAnalysis and companyIntelligence are now managed by JobApplicationForm (Step 2)
  // Company intelligence fetching removed - JobApplicationForm handles it

  // Reset message box when wizard closes to prevent blinking
  // Use a ref to track messageBox.show to avoid including it in dependencies (prevents flickering)
  const messageBoxShowRef = useRef(false);
  useEffect(() => {
    messageBoxShowRef.current = messageBox.show;
  }, [messageBox.show]);

  useEffect(() => {
    if (!isOpen && messageBoxShowRef.current) {
      hideMessageBox();
    }
  }, [isOpen]); // Only depend on isOpen to prevent flickering from unstable hideMessageBox reference

  // Handle prop changes to update internal state
  useEffect(() => {
    // Only update state when wizard is open to prevent unnecessary re-renders
    if (!isOpen) {
      // Reset refs when wizard closes
      lastInitializedAppIdRef.current = null;
      lastInitializedViewModeRef.current = false;
      return;
    }

    // Prevent re-initialization if we've already initialized with the same application
    // BUT allow updates if formData.id was set (e.g., after auto-save) or if we're switching apps
    const currentAppId = editingApplication?.id || formData.id || null;
    const hasNewFormDataId =
      formData.id && formData.id !== lastInitializedAppIdRef.current;
    const isSameApp =
      currentAppId === lastInitializedAppIdRef.current && currentAppId !== null;
    const isSameViewMode = viewMode === lastInitializedViewModeRef.current;

    // Skip only if same app, same view mode, AND no new formData.id was set
    // BUT always allow initialization if we don't have an app ID yet (new application)
    if (
      isSameApp &&
      isSameViewMode &&
      !hasNewFormDataId &&
      currentAppId !== null
    ) {
      // Already initialized with this application and view mode, skip
      return;
    }

    if (editingApplication) {
      setFormData({
        id: editingApplication.id || formData.id || undefined, // Preserve existing formData.id if editingApplication doesn't have one
        url: editingApplication.url || "",
        description: editingApplication.description || "",
        company: editingApplication.company || null,
        position: editingApplication.position || null,
        positionCore: editingApplication.positionCore || null,
        positionSpecialization: editingApplication.positionSpecialization || [],
        salary: editingApplication.salary || null,
        location: editingApplication.location || null,
        workType: editingApplication.workType || null,
        employmentType: editingApplication.employmentType || null,
        contactInfo: editingApplication.contactInfo || null,
        recruiter: editingApplication.recruiter || null,
        jobLevel: editingApplication.jobLevel || null,
        jobLevelNormalized: editingApplication.jobLevelNormalized || null,
        jobLevelConfidence: editingApplication.jobLevelConfidence || null,
        jobLevelSource: editingApplication.jobLevelSource || null,
        fitScore: editingApplication.fitScore ?? undefined,
        jobSummary: editingApplication.jobSummary || null,
        responsibilities: editingApplication.responsibilities || [],
        requiredQualifications: editingApplication.requiredQualifications || [],
        optionalQualifications: editingApplication.optionalQualifications || [],
        additionalQualifications:
          editingApplication.additionalQualifications || [],
        education: editingApplication.education || [],
        technologies: editingApplication.technologies || [],
        benefits: editingApplication.benefits || [],
        benefitsStructured: editingApplication.benefitsStructured || {
          healthInsurance: [],
          retirement: [],
          timeOff: [],
          allowances: [],
          memberships: [],
          bonusesEquity: [],
          other: [],
        },
        postingDate: editingApplication.postingDate || null,
        postingAge: editingApplication.postingAge || null,
        applicantsCount: editingApplication.applicantsCount || null,
        applicantsText: editingApplication.applicantsText || null,
        status: editingApplication.status || "Pending",
        appliedDate:
          editingApplication.appliedDate ||
          new Date().toISOString().split("T")[0],
        notes: editingApplication.notes ?? undefined,
        contacts: editingApplication.contacts || [],
      });

      // Set appropriate step based on view mode
      setCurrentStep(2);

      // Track that we've initialized with this application
      // Use formData.id if it exists (from auto-save), otherwise use editingApplication.id
      lastInitializedAppIdRef.current =
        formData.id || editingApplication.id || null;
      lastInitializedViewModeRef.current = viewMode;
    } else {
      // Reset form data for new application
      if (lastInitializedAppIdRef.current) {
        // Was editing; parent cleared `editingApplication` → start a fresh application
        setFormData({
          url: "",
          description: "",
          company: null,
          position: null,
          positionCore: null,
          positionSpecialization: [],
          salary: null,
          location: null,
          workType: null,
          employmentType: null,
          contactInfo: null,
          recruiter: null,
          jobLevel: null,
          jobLevelNormalized: null,
          jobLevelConfidence: null,
          jobLevelSource: null,
          jobSummary: null,
          responsibilities: [],
          requiredQualifications: [],
          optionalQualifications: [],
          additionalQualifications: [],
          education: [],
          technologies: [],
          benefits: [],
          benefitsStructured: {
            healthInsurance: [],
            retirement: [],
            timeOff: [],
            allowances: [],
            memberships: [],
            bonusesEquity: [],
            other: [],
          },
          postingDate: null,
          postingAge: null,
          applicantsCount: null,
          applicantsText: null,
          status: "Pending",
          appliedDate: new Date().toISOString().split("T")[0],
          notes: undefined,
          contacts: [],
        });
        setCurrentStep(1);
        lastInitializedAppIdRef.current = null;
      } else if (!formData.id) {
        setFormData({
          url: "",
          description: "",
          company: null,
          position: null,
          positionCore: null,
          positionSpecialization: [],
          salary: null,
          location: null,
          workType: null,
          employmentType: null,
          contactInfo: null,
          recruiter: null,
          jobLevel: null,
          jobLevelNormalized: null,
          jobLevelConfidence: null,
          jobLevelSource: null,
          jobSummary: null,
          responsibilities: [],
          requiredQualifications: [],
          optionalQualifications: [],
          additionalQualifications: [],
          education: [],
          technologies: [],
          benefits: [],
          benefitsStructured: {
            healthInsurance: [],
            retirement: [],
            timeOff: [],
            allowances: [],
            memberships: [],
            bonusesEquity: [],
            other: [],
          },
          postingDate: null,
          postingAge: null,
          applicantsCount: null,
          applicantsText: null,
          status: "Pending",
          appliedDate: new Date().toISOString().split("T")[0],
          notes: undefined,
          contacts: [],
        });
        setCurrentStep(1);
      }

      lastInitializedViewModeRef.current = viewMode;
    }
  }, [isOpen, editingApplication?.id, viewMode]); // Don't include formData.id to prevent flickering - guard logic handles id changes

  const handleInputChange = useCallback(
    (
      field: keyof JobApplication,
      value: string | string[] | Education[] | null,
    ) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    [],
  );

  const handleNext = useCallback(async () => {
    if (currentStep === 1) {
      // Process job description with OpenAI
      setIsProcessing(true);
      try {
        // Compact on frontend before send (same as Intelligent Search / extension)
        const toParse = compactJobDescriptionForParsing(
          formData.description || "",
        );
        const extractedData =
          await secureOpenAIService.parseJobDescription(toParse);
        const { warnings: extractedWarnings, ...extractedFields } =
          extractedData;

        if (extractedWarnings && extractedWarnings.length > 0) {
          const primaryWarning = extractedWarnings[0];
          const extraCount = extractedWarnings.length - 1;
          const title = "Submission Instructions Detected";
          const message =
            extraCount > 0
              ? `${primaryWarning.message} (${extraCount} more warning${extraCount > 1 ? "s" : ""})`
              : primaryWarning.message;
          const description = primaryWarning.evidence
            ? `Detected text: "${primaryWarning.evidence}"`
            : undefined;

          showMessageBox({
            type: "warning",
            title,
            message,
            description,
            buttons: ["ok"],
            onButtonClick: () => {},
          });
        }

        const updatedFormData = {
          ...formData,
          ...extractedFields,
        };

        // Auto-save application to get an ID for fit analysis (only for new applications)
        if (!editingApplication) {
          // Check if we already have an initialized application ID
          const existingInitializedId =
            updatedFormData.id && updatedFormData.status === "Initialized"
              ? updatedFormData.id
              : null;

          try {
            const applicationToSave: JobApplication = {
              id:
                existingInitializedId ||
                `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              url: updatedFormData.url || "",
              company: updatedFormData.company || null,
              position: updatedFormData.position || null,
              positionCore: updatedFormData.positionCore || null,
              positionSpecialization:
                updatedFormData.positionSpecialization || [],
              salary: updatedFormData.salary || null,
              location: updatedFormData.location || null,
              workType: updatedFormData.workType || null,
              employmentType: updatedFormData.employmentType || null,
              contactInfo: updatedFormData.contactInfo || null,
              recruiter: updatedFormData.recruiter || null,
              jobLevel: updatedFormData.jobLevel || null,
              jobLevelNormalized: updatedFormData.jobLevelNormalized || null,
              jobLevelConfidence: updatedFormData.jobLevelConfidence || null,
              jobLevelSource: updatedFormData.jobLevelSource || null,
              jobSummary: updatedFormData.jobSummary || null,
              responsibilities: updatedFormData.responsibilities || [],
              requiredQualifications:
                updatedFormData.requiredQualifications || [],
              optionalQualifications:
                updatedFormData.optionalQualifications || [],
              additionalQualifications:
                updatedFormData.additionalQualifications || [],
              education: updatedFormData.education || [],
              technologies: updatedFormData.technologies || [],
              benefits: updatedFormData.benefits || [],
              benefitsStructured: updatedFormData.benefitsStructured || {
                healthInsurance: [],
                retirement: [],
                timeOff: [],
                allowances: [],
                memberships: [],
                bonusesEquity: [],
                other: [],
              },
              postingDate: updatedFormData.postingDate || null,
              postingAge: updatedFormData.postingAge || null,
              applicantsCount: updatedFormData.applicantsCount || null,
              applicantsText: updatedFormData.applicantsText || null,
              description: updatedFormData.description || "",
              status: "Initialized", // Auto-save with Initialized status
              appliedDate:
                updatedFormData.appliedDate ||
                new Date().toISOString().split("T")[0],
              notes: updatedFormData.notes ?? undefined,
              contacts: updatedFormData.contacts || [], // Include contacts from parsed data
            };

            // Save to backend to get ID and trigger fit analysis
            // Backend will reuse existing initialized application if it exists
            const savedApplication =
              await jobApplicationService.createApplication(applicationToSave);

            // Use the ID from the API response (might be different from generated ID)
            const actualApplicationId =
              savedApplication?.id || applicationToSave.id;

            // Update formData with extracted data AND the ID from saved application
            setFormData((prev) => ({
              ...prev,
              ...extractedData,
              id: actualApplicationId,
              status: "Initialized", // Ensure status is set to Initialized
            }));

            // Wait longer for fit analysis to start processing (it's async)
            await new Promise((resolve) => setTimeout(resolve, 2000));
          } catch (saveError) {
            console.error(
              "Error auto-saving application for fit analysis:",
              saveError,
            );
            // Continue anyway - update formData without ID, fit analysis will be available after manual save
            setFormData((prev) => ({
              ...prev,
              ...extractedData,
            }));
          }
        } else {
          // Just update formData with extracted data
          setFormData((prev) => ({
            ...prev,
            ...extractedData,
          }));
        }

        // Company intelligence will be fetched by JobApplicationForm when Step 2 loads
        setCurrentStep(2);
      } catch (error) {
        console.error("Error processing job description:", error);
        // Show error message to user using MessageBox
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred";
        showMessageBox({
          type: "error",
          title: "Job Analysis Failed",
          message: `Failed to analyze job description: ${errorMessage}. Please check your connection and try again, or enter the information manually.`,
          buttons: ["ok"],
          onButtonClick: () => {},
        });
      } finally {
        setIsProcessing(false);
      }
    }
  }, [currentStep, formData.description, formData.url, showMessageBox]); // editingApplication and formData not needed - only specific fields used

  const handleBack = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const handleClose = useCallback(() => {
    setCurrentStep(1);
    setFormData({
      url: "",
      description: "",
      company: null,
      position: null,
      positionCore: null,
      positionSpecialization: [],
      salary: null,
      location: null,
      workType: null,
      employmentType: null,
      contactInfo: null,
      recruiter: null,
      jobLevel: null,
      jobLevelNormalized: null,
      jobLevelConfidence: null,
      jobLevelSource: null,
      jobSummary: null,
      responsibilities: [],
      requiredQualifications: [],
      optionalQualifications: [],
      additionalQualifications: [],
      education: [],
      technologies: [],
      benefits: [],
      benefitsStructured: {
        healthInsurance: [],
        retirement: [],
        timeOff: [],
        allowances: [],
        memberships: [],
        bonusesEquity: [],
        other: [],
      },
      postingDate: null,
      postingAge: null,
      applicantsCount: null,
      applicantsText: null,
      status: "Pending",
      appliedDate: new Date().toISOString().split("T")[0],
      notes: undefined,
    });
    onClose();
  }, [onClose]);

  const handleSave = useCallback(() => {
    // Determine ID - use formData.id (from auto-save) or editingApplication.id, or generate new
    const applicationId =
      formData.id ||
      editingApplication?.id ||
      `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Determine status - preserve existing status when editing, set to Pending for new applications
    let finalStatus: JobApplication["status"];
    if (editingApplication) {
      // When editing, preserve the existing status (user can change it themselves)
      finalStatus = formData.status || editingApplication.status || "Pending";
    } else {
      // New application: set to Pending (if it was Initialized, backend will update TTL)
      finalStatus =
        formData.status === "Initialized"
          ? "Pending"
          : formData.status || "Pending";
    }

    const application: JobApplication = {
      id: applicationId,
      url: formData.url || "",
      company: formData.company || null,
      position: formData.position || null,
      positionCore: formData.positionCore || null,
      positionSpecialization: formData.positionSpecialization || [],
      salary: formData.salary || null,
      location: formData.location || null,
      workType: formData.workType || null,
      employmentType: formData.employmentType || null,
      contactInfo: formData.contactInfo || null,
      recruiter: formData.recruiter || null,
      jobLevel: formData.jobLevel || null,
      jobLevelNormalized: formData.jobLevelNormalized || null,
      jobLevelConfidence: formData.jobLevelConfidence || null,
      jobLevelSource: formData.jobLevelSource || null,
      jobSummary: formData.jobSummary || null,
      responsibilities: formData.responsibilities || [],
      requiredQualifications: formData.requiredQualifications || [],
      optionalQualifications: formData.optionalQualifications || [],
      additionalQualifications: formData.additionalQualifications || [],
      education: formData.education || [],
      technologies: formData.technologies || [],
      benefits: formData.benefits || [],
      benefitsStructured: formData.benefitsStructured || {
        healthInsurance: [],
        retirement: [],
        timeOff: [],
        allowances: [],
        memberships: [],
        bonusesEquity: [],
        other: [],
      },
      postingDate: formData.postingDate || null,
      postingAge: formData.postingAge || null,
      applicantsCount: formData.applicantsCount || null,
      applicantsText: formData.applicantsText || null,
      description: formData.description || "",
      status: finalStatus,
      appliedDate:
        formData.appliedDate || new Date().toISOString().split("T")[0],
      notes: formData.notes ?? undefined,
      contacts: formData.contacts || [], // Include contacts
    };

    onSave(application);
    onClose();
  }, [formData, editingApplication, onSave, onClose]);

  // Track when modal opens to control animation (only animate on first open)
  useEffect(() => {
    if (isOpen) {
      // Trigger animation when modal opens
      setShouldAnimate(true);
      // Remove animation class after animation completes to prevent re-animation on re-renders
      const timer = setTimeout(() => {
        setShouldAnimate(false);
      }, 300); // Match animation duration
      return () => clearTimeout(timer);
    } else {
      // Reset when modal closes
      setShouldAnimate(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="wizard-overlay">
      <div
        className={`wizard-modal ${shouldAnimate ? "wizard-modal-opened" : ""}`}
      >
        <div className="wizard-header">
          <h2 className="wizard-title">
            {editingApplication ? "Edit Application" : "Add New Application"}
          </h2>
          <button className="wizard-close" onClick={handleClose}>
            ✕
          </button>
        </div>

        <div className="wizard-progress">
          <div className={`progress-step ${currentStep >= 1 ? "active" : ""}`}>
            <div className="step-number">1</div>
            <div className="step-label">Job Details</div>
          </div>
          <div className={`progress-step ${currentStep >= 2 ? "active" : ""}`}>
            <div className="step-number">2</div>
            <div className="step-label">Review &amp; Edit</div>
          </div>
        </div>

        <div className="wizard-content">
          {currentStep === 1 && (
            <div className="wizard-step">
              <div className="step-content">
                <div className="form-group">
                  <label htmlFor="job-url" className="form-label">
                    Job Posting URL
                  </label>
                  <input
                    id="job-url"
                    type="url"
                    className="form-input"
                    placeholder="https://company.com/job-posting"
                    value={formData.url || ""}
                    onChange={(e) => handleInputChange("url", e.target.value)}
                    readOnly={viewMode}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="job-description" className="form-label">
                    Job Description
                  </label>
                  <textarea
                    id="job-description"
                    className="form-textarea"
                    placeholder="Paste the complete job description here..."
                    value={formData.description || ""}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                    readOnly={viewMode}
                    rows={12}
                  />
                  <div className="form-help">
                    Paste the full job description and we'll extract all the
                    important details for you.
                  </div>
                </div>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <JobApplicationForm
              jobData={formData as JobApplication}
              onSave={handleSave}
              onClose={handleClose}
              onBack={handleBack}
              viewMode={viewMode}
              isProcessing={isProcessing}
            />
          )}
        </div>

        {/* Only show wizard footer for Step 1 - Step 2 uses JobApplicationForm's footer */}
        {currentStep === 1 && (
          <div className="wizard-footer">
            <div className="wizard-buttons">
              {viewMode ? (
                <button className="btn btn-primary" onClick={onClose}>
                  Close
                </button>
              ) : (
                <button
                  className="btn btn-primary"
                  onClick={handleNext}
                  disabled={isProcessing || !formData.description?.trim()}
                >
                  {isProcessing ? "Processing..." : "Next"}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Render MessageBox only when it should be shown to prevent blinking */}
      {messageBox.show && <MessageBox {...messageBox} />}
    </div>
  );
};
